import { Suspense, useEffect, useRef } from 'react';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { isPendingUpdate } from '@/lib/sw-update';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@scio3/ui';
import { TopNavigation } from './TopNavigation';
import { PromotionalBanner } from './PromotionalBanner';
import { useLanguage } from '@/hooks/useLanguage';
import { useFolderDeleteCacheEviction } from '@/hooks/useFolderDeleteCacheEviction';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useMatomoPageTracking } from '@/lib/matomo';
import { signOut, auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { STORAGE_KEY } from '@/lib/i18n';
import {
  isInIframeContext,
  startParentListener,
  notifyNavigation,
} from '@/lib/iframe-child-bridge';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { CanvasAuthBridge } from '@/components/iframe/CanvasAuthBridge';
import { GetStartedChecklist } from '@/features/get-started';
import { OptiBotFAB, OptiBotPanel } from '@/features/optibot';
import { useOptibotStore } from '@/features/optibot/stores/optibot-store';
import { InvitationAcceptanceDialog } from '@/features/auth/components/InvitationAcceptanceDialog';
import { InactiveAccountDialog } from './InactiveAccountDialog';
import { BillingDialogs } from '@/features/billing/dialogs/BillingDialogs';
import { ForcePasswordChangeDialog } from './ForcePasswordChangeDialog';
import { useShouldShowOnboarding } from '@/features/survey/hooks/useShouldShowOnboarding';
import { PersistentCanvasFrame } from '@/features/templates/components/PersistentCanvasFrame';

import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { clearPersistedCache } from '@scio3/graphql-client';

// Lazy-loaded: SmallAppEditDialog pulls the entire asset editing pipeline
// (Uppy, Transloadit, SmallApp microservice, SelectAssetModal ~500+ kB).
// It only renders when a user edits a SmallApp, so lazy-load is safe.
const SmallAppEditDialog = lazyWithRetry(() =>
  import('@/features/assets/components/dialogs/SmallAppEditDialog').then((m) => ({
    default: m.SmallAppEditDialog,
  })),
);

// Lazy-loaded: OnboardingForm pulls react-hook-form (~109 kB).
// Only shown once for new account owners after signup.
const OnboardingForm = lazyWithRetry(() =>
  import('@/features/survey/components/OnboardingForm').then((m) => ({
    default: m.OnboardingForm,
  })),
);

const useNativeTemplates = import.meta.env.VITE_USE_NATIVE_TEMPLATES === 'true';

const AUTH_ROUTES = [
  '/signIn',
  '/signUp',
  '/password',
  '/verify',
  '/join',
  '/sso',
  '/auth/sso-login',
  '/sign-email-link',
];

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isAuthPage = AUTH_ROUTES.some((r) => location.pathname.startsWith(r));
  // Pages that use full-width layout (no horizontal padding on page-wrapper)
  const fullWidthPrefixes = [
    '/app/s/',
    '/app/screenManagement',
    '/app/assetManagement',
    '/app/playlistManagement',
    '/app/scheduleManagement',
    '/app/templateManagement',
    '/app/designManagement',
    '/app/engageManagement',
    '/app/optidev',
    '/app/playbackReport',
    '/app/ai-analytics',
  ];
  const isFullWidthPage = fullWidthPrefixes.some((p) => location.pathname.startsWith(p));
  // Reactive: true only after confirmed handshake with SCIO 1.0 parent
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const token = useAuthStore((s) => s.token);
  const loading = useAuthStore((s) => s.loading);
  const me = useAuthStore((s) => s.me);
  const isOptiBotOpen = useOptibotStore((s) => s.isOpen);
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  const needsPasswordChange = useAuthStore((s) => s.needsPasswordChange);
  const inviteUser = useAuthStore((s) => s.inviteUser);
  const pendingOnboarding = useAuthStore((s) => s.pendingOnboarding);
  const showOnboarding = useShouldShowOnboarding();

  // Sync i18next language with user profile on auth load
  useLanguage();
  // Cascade-evict cached entities when a folder is deleted by another user (single app-level listener)
  useFolderDeleteCacheEviction();
  // Set document.title based on current route (e.g. "OptiSigns – Screens Management")
  usePageTitle();
  // Track page views in Matomo on every route change (production only)
  useMatomoPageTracking();

  // Reload on route change when a new version is pending.
  // prevPathRef starts as current path so initial mount doesn't trigger.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    if (!isPendingUpdate()) return;

    console.warn('[sw-update] Route changed with pending update, reloading');
    window.location.href = location.pathname + location.search;
  }, [location.pathname, location.search]);

  // When in iframe context: listen for parent commands (navigation, language, team, logout).
  // Uses static isInIframeContext so listener is registered early (before handshake completes).
  useEffect(() => {
    if (!isInIframeContext) return;

    const cleanup = startParentListener({
      onRouterRedirect: (url) => {
        // Parent sends a path like "/app/screenManagement" or a state name
        const path = url.startsWith('/') ? url : `/app/${url}`;
        navigate(path);
      },
      onChangeTeam: (teamId) => {
        // Clear team-scoped persisted data before switching teams
        clearPersistedPaths();
        clearPersistedCache();
        useTeamStore.getState().setActiveTeam(teamId);
        // Reload page to clear in-memory Apollo cache and reset all stores
        // (mirrors TeamSwitcher behavior for consistent team isolation)
        window.location.reload();
      },
      onSyncLanguage: (language) => {
        if (language !== i18n.language) {
          i18n.changeLanguage(language);
          localStorage.setItem(STORAGE_KEY, language);
        }
      },
      onLogout: () => {
        signOut(auth);
        navigate('/signIn');
      },
      onReload: () => {
        window.location.reload();
      },
    });

    return cleanup;
  }, [navigate, i18n]);

  // When embedded: notify parent of route changes
  useEffect(() => {
    if (!isEmbedded) return;
    notifyNavigation(location.pathname + location.search);
  }, [isEmbedded, location.pathname, location.search]);

  if (isAuthPage) {
    // Redirect authenticated users away from auth pages.
    // Require `me` (not just token) — token alone doesn't mean the user is fully authenticated.
    // Prevents flashy redirect when login fails with INVALID_TEAM (token is briefly set by
    // onAuthStateChanged before forceLogout clears it).
    // Skip redirect when pendingOnboarding is true — SignupPage is rendering
    // the onboarding form in-place and should not be navigated away from.
    // Exception: /verify must always render — applyActionCode() needs to run regardless of auth state.
    // /password/reset also needs to render for authenticated users who click reset links.
    const isActionPage =
      location.pathname.startsWith('/verify') || location.pathname.startsWith('/password/reset');
    if (!loading && token && me && !isEmbedded && !pendingOnboarding && !isActionPage) {
      return <Navigate to="/app/screenManagement" replace />;
    }
    return <Outlet />;
  }

  // Gate on full authentication before rendering any app chrome (TopNavigation, sidebar).
  // Mirrors the `isAuthPage` redirect condition above: require `me` not just `token`,
  // because token is set by onAuthStateChanged but me is only set after ME_QUERY succeeds.
  // This prevents any app chrome from flashing for unauthenticated users.
  // - loading: still resolving — show spinner with bg-background (avoids black bar flash)
  // - !me && !isInIframeContext: auth settled but not authenticated — redirect to signIn
  //   (iframe waits for parent custom-token handshake, so we skip the redirect there)
  if (loading || !me) {
    if (!me && !loading && !isInIframeContext) {
      return <Navigate to="/signIn" state={{ from: location }} replace />;
    }
    return (
      <div className="bg-background flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Account payment lapsed — blocking dialog, no app access.
  // Skip when embedded (scio1 parent handles it).
  // InactiveAccountDialog lazy-mounts the scio1 modal adapter iframe only when the user
  // clicks Update — this prevents scio1's own inactive modal from appearing simultaneously.
  if (isAccountInactive && !isEmbedded) {
    return <InactiveAccountDialog />;
  }

  // Admin flagged user to change password on next sign-in — block until done.
  // Skip when embedded (scio1 parent handles it via createPasswordModal).
  if (needsPasswordChange && !isEmbedded) {
    return <ForcePasswordChangeDialog />;
  }

  // New account owner onboarding — show survey before app access.
  // No skeleton needed: SignupPage prefetches all data before navigating,
  // so the form renders instantly after signup.
  if (showOnboarding) {
    return (
      <Suspense>
        <OnboardingForm />
      </Suspense>
    );
  }

  return (
    <div id="wrapper" className="flex h-screen flex-col overflow-hidden bg-background">
      {!isInIframeContext && <TopNavigation />}
      {!isInIframeContext && <PromotionalBanner />}
      <div className="flex min-h-0 flex-1">
        <div
          id="page-wrapper"
          className={cn(
            'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden [&>*]:min-h-0 [&>*]:flex-1 [&>*]:overflow-hidden',
            !isFullWidthPage && (isOptiBotOpen ? 'pl-3 md:pl-6' : 'px-3 md:px-6'),
          )}
        >
          <Outlet />
          {useNativeTemplates && <PersistentCanvasFrame />}
        </div>
        {import.meta.env.VITE_ENABLE_OPTIBOT === 'true' && !isInIframeContext && <OptiBotPanel />}
      </div>
      {/* Mount modal adapter on all pages so openSubscriptionPlan works everywhere,
          including scio1-iframe settings pages (account-members, billing, credentials, etc.) */}
      <Scio1Frame mode="modal" />
      {import.meta.env.VITE_USE_NATIVE_BILLING === 'true' && <BillingDialogs />}
      <Suspense>
        <SmallAppEditDialog />
      </Suspense>
      <CanvasAuthBridge />
      {!isInIframeContext && <GetStartedChecklist />}
      {inviteUser && <InvitationAcceptanceDialog />}
      {import.meta.env.VITE_ENABLE_OPTIBOT === 'true' && !isInIframeContext && <OptiBotFAB />}
    </div>
  );
}
